import { useEffect, useMemo, useState } from 'react';
import { useDynamicFilter } from '@/features/RentersInsurance/hooks/useDynamicFilter';
import { OptionType, SearchableFilterField } from './SearchableFilterField';
import { usePropertyList } from '@/features/RentersInsurance/api/list-properties.api';

const FILTER_KEY = 'propertyIds';

export function PropertyFilter() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { getFilterOptions, handleFilterChange, getNumberOfSelectedFilterItems } =
    useDynamicFilter(FILTER_KEY);
  const [options, setOptions] = useState<OptionType[]>([]);

  const {
    data: propertyList,
    status,
    fetchStatus,
  } = usePropertyList(
    {
      search,
      limit: 1000,
      page: 1,
    },
    {
      enabled: open,
    },
  );

  const propertyListArray = useMemo(() => propertyList?.result || [], [propertyList?.result]);

  const mappedPropertyList = propertyListArray.map((property) => ({
    ...property,
    marketingName: property.marketingName ?? '',
    name: property.name ?? '-',
  }));

  const visibleOptions = getFilterOptions(mappedPropertyList);
  const isInitialLoading = status === 'pending';
  const isLoading = isInitialLoading || fetchStatus === 'fetching';

  useEffect(() => {
    if (!isInitialLoading) {
      setOptions(visibleOptions);
    }
  }, [isInitialLoading]);

  function handleOptionSelect(optionValue: OptionType) {
    const newOptions = options.map((option) =>
      option.id === optionValue.id ? { ...option, selected: !option.selected } : option,
    );

    setOptions(newOptions);
    handleFilterChange(newOptions);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function handleFilterClear() {
    const newOptions = options.map((option) => ({ ...option, selected: false }));

    setSearch('');
    setOptions(newOptions);
    handleFilterChange(newOptions);
  }

  function handleOpen() {
    setOpen(true);
  }

  function handleClose() {
    setSearch('');
  }

  function handleAllVisibleOptionsSelect() {
    const newOptions = options.map((option) => {
      const visibleOptionToMakeSelected = visibleOptions.find(
        (visibleOption) => visibleOption.id === option.id,
      );

      return visibleOptionToMakeSelected ? { ...option, selected: true } : option;
    });

    setOptions(newOptions);
    handleFilterChange(newOptions);
  }

  function handleVisibleOptionsClear() {
    const newOptions = options.map((option) => {
      const visibleOptionToMakeSelected = visibleOptions.find(
        (visibleOption) => visibleOption.id === option.id,
      );

      return visibleOptionToMakeSelected ? { ...option, selected: false } : option;
    });

    setOptions(newOptions);
    handleFilterChange(newOptions);
  }

  const numberOfSelectedFilterItems = getNumberOfSelectedFilterItems();

  return (
    <SearchableFilterField
      id="properties"
      title="Properties"
      search={search}
      options={options}
      isLoading={isLoading}
      visibleOptions={visibleOptions}
      numberOfSelectedOptions={numberOfSelectedFilterItems}
      onOpen={handleOpen}
      onClose={handleClose}
      onAllVisibleOptionsSelect={handleAllVisibleOptionsSelect}
      onVisibleOptionsClear={handleVisibleOptionsClear}
      onOptionsClear={handleFilterClear}
      onOptionSelect={handleOptionSelect}
      onSearch={handleSearch}
    />
  );
}
